import React from 'react';
import moment from 'moment';
import { DateFormats } from 'constants/common';
import { SxProps, Theme } from '@mui/material/styles';
import { Box, Typography, Tooltip, Stack } from '@mui/material';
import { DealActivityEventPriority, DealActivityStatus } from '@trnsact/trnsact-shared-types';

import { CalendarEvent } from './types';
import { COMPACT_MAX_MINUTES, PRIORITY_CONFIG, STATUS_CONFIG } from './constants';

export const getPriorityColor =
  (priority?: DealActivityEventPriority | null) =>
  (theme: Theme): string =>
    priority ? PRIORITY_CONFIG[priority].color(theme) : theme.palette.divider;

interface Props {
  event: CalendarEvent;
  sx?: SxProps<Theme>;
  showMore?: boolean;
}

export const CalendarEventCard = ({ event, sx, showMore = false }: Props) => {
  const { title, status, allDay, start, durationMinutes } = event;

  const statusView = STATUS_CONFIG[status ?? DealActivityStatus.None];
  const isCancelledOrClosed = status === DealActivityStatus.Closed || status === DealActivityStatus.Cancelled;

  const isCompact = !allDay && durationMinutes <= COMPACT_MAX_MINUTES;
  const timeLabel = !allDay && start ? moment(start).format(DateFormats.Time12Hour) : null;

  const StatusIcon = statusView.icon;

  return (
    <Box
      sx={[
        theme => ({
          display: 'flex',
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: theme.spacing(0.5),
          padding: theme.spacing(0, 0.5),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {isCompact ? (
        <Stack direction="row" gap="0.2rem" alignItems="center">
          <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>
            {timeLabel}
          </Typography>

          <Typography
            noWrap
            variant="caption"
            sx={{ color: 'black', textDecoration: isCancelledOrClosed ? 'line-through' : 'none' }}
          >
            {title}
          </Typography>
        </Stack>
      ) : (
        <Stack>
          <Stack direction="row" gap="0.2rem" alignItems="center">
            <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>
              {timeLabel}
            </Typography>

            <Typography
              noWrap
              variant="caption"
              sx={{ color: 'black', textDecoration: isCancelledOrClosed ? 'line-through' : 'none' }}
            >
              {title}
            </Typography>
          </Stack>

          {showMore && (
            <Stack direction="row" alignItems="center" gap="0.3rem">
              <Tooltip title={statusView.label}>
                <StatusIcon sx={{ fontSize: 14 }} color={statusView.color} />
              </Tooltip>

              <Typography variant="caption" sx={{ color: 'black' }}>
                {statusView.label}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
};
